import { useEffect, useMemo, useState } from 'react'
import API from 'app/api'

const apiDomain = process.env.REACT_APP_API_DOMAIN || ''

function safeUrl(url) {
    try {
        return new URL(url)
    } catch {
        return null
    }
}

const apiOrigin = safeUrl(apiDomain)?.origin || ''

// bucket.s3.amazonaws.com, bucket.s3.us-east-1.amazonaws.com,
// bucket.s3-us-west-2.amazonaws.com, s3.amazonaws.com, ...
function isS3Host(hostname) {
    const host = hostname.toLowerCase()
    return host.endsWith('.amazonaws.com') && /(^|.)s3[.-]/.test(host)
}

export function isProtectedImageUrl(src) {
    if (!src || typeof src !== 'string') return false

    // already-resolved or inline sources
    if (src.startsWith('blob:')) return false
    if (src.startsWith('data:')) return false

    // static public assets should be loaded directly by the browser
    if (src.startsWith('/assets/images/')) return false

    // relative paths are assumed to be our app/API
    if (src.startsWith('/')) return true

    // absolute URLs: decide on the parsed hostname/origin, never on substrings
    // of the raw string, so a host like evil.example/x.s3.amazonaws.com/ cannot
    // be mistaken for S3 (CodeQL js/incomplete-url-substring-sanitization)
    const parsed = safeUrl(src)
    if (!parsed) return false

    // S3 signed URLs can be used directly
    if (isS3Host(parsed.hostname)) return false

    // only treat as protected if it targets our API origin
    if (!apiOrigin) return false

    return parsed.origin === apiOrigin
}

async function tryExtractUrlFromBlob(blob) {
    // Handles: plain text URL or JSON like { url: "https://..." }
    const text = (await blob.text()).trim()
    if (!text) return null

    // JSON?
    try {
        const obj = JSON.parse(text)
        const maybeUrl =
            obj?.url ||
            obj?.signedUrl ||
            obj?.location ||
            obj?.data?.url ||
            obj?.data?.signedUrl

        if (typeof maybeUrl === 'string' && maybeUrl.trim()) {
            return maybeUrl.trim()
        }
    } catch {
        // not JSON
    }

    // raw string URL
    try {
        const u = new URL(text)
        return u.toString()
    } catch {
        return null
    }
}

// Presigned S3 URLs (and our GetImage indirection) expire, so cached entries
// are only trusted for a window. After that we re-resolve from the API.
const CACHE_TTL_MS = 45 * 60 * 1000

// Module-level caches shared across every AuthImg/useAuthImageSrc consumer.
// resolvedCache: src -> { url, ts, isBlob } — a settled result.
// inflight:      src -> Promise<{ url, isBlob }> — an in-progress resolution,
// so concurrent identical tiles share one network request instead of N.
const resolvedCache = new Map()
const inflight = new Map()

function getFreshCacheEntry(src) {
    const entry = resolvedCache.get(src)
    if (!entry) return null
    if (Date.now() - entry.ts > CACHE_TTL_MS) {
        resolvedCache.delete(src)
        return null
    }
    return entry
}

// Resolve a protected src to a directly-usable URL (S3 link or blob object URL).
// Deduped via `inflight` and memoized via `resolvedCache`. The AbortController
// signal only cancels *this* caller's interest — the shared fetch runs to
// completion so other waiters still get a result.
function resolveProtectedSrc(src, signal) {
    const cached = getFreshCacheEntry(src)
    if (cached) return Promise.resolve(cached)

    let promise = inflight.get(src)
    if (!promise) {
        promise = (async () => {
            const response = await API.get(src, { responseType: 'blob' })

            const blob = response?.data
            if (!blob) {
                throw new Error('Image response missing blob')
            }

            const contentType =
                response?.headers?.['content-type'] ||
                response?.headers?.['Content-Type'] ||
                blob?.type ||
                ''

            // Some endpoints return a signed S3 URL (text/plain or
            // application/json) instead of the image bytes. Use the URL directly.
            if (!String(contentType).toLowerCase().startsWith('image/')) {
                const extractedUrl = await tryExtractUrlFromBlob(blob)
                if (extractedUrl) {
                    return { url: extractedUrl, isBlob: false }
                }
                throw new Error(
                    `Unexpected content-type for image response: ${contentType || '(none)'}`
                )
            }

            // Real image bytes — wrap in an object URL we keep for the session.
            return { url: URL.createObjectURL(blob), isBlob: true }
        })()
            .then((result) => {
                resolvedCache.set(src, {
                    url: result.url,
                    isBlob: result.isBlob,
                    ts: Date.now(),
                })
                return result
            })
            .finally(() => {
                inflight.delete(src)
            })
        inflight.set(src, promise)
    }

    if (!signal) return promise

    // Race the shared resolution against this caller's abort signal so an
    // unmounted/changed consumer stops waiting without cancelling the shared work.
    return new Promise((resolve, reject) => {
        const onAbort = () => reject(new DOMException('Aborted', 'AbortError'))
        if (signal.aborted) {
            onAbort()
            return
        }
        signal.addEventListener('abort', onAbort, { once: true })
        promise
            .then(resolve, reject)
            .finally(() => signal.removeEventListener('abort', onAbort))
    })
}

export default function useAuthImageSrc(src, { fallbackSrc, enabled = true } = {}) {
    const shouldFetch = useMemo(() => isProtectedImageUrl(src), [src])

    // Never seed a protected URL into the <img>: doing so makes the browser
    // request it with no Authorization header → a wasted 401. Show the fallback
    // until the authed resolution completes.
    const initialSrc = () => {
        if (!src) return fallbackSrc || ''
        if (!isProtectedImageUrl(src)) return src
        const cached = getFreshCacheEntry(src)
        return cached ? cached.url : fallbackSrc || ''
    }

    const [resolvedSrc, setResolvedSrc] = useState(initialSrc)
    const [error, setError] = useState(null)
    const [isLoading, setIsLoading] = useState(
        () => Boolean(src && isProtectedImageUrl(src) && enabled && !getFreshCacheEntry(src))
    )

    useEffect(() => {
        const controller = new AbortController()
        setError(null)

        if (!src) {
            setIsLoading(false)
            setResolvedSrc(fallbackSrc || '')
            return () => controller.abort()
        }

        if (!shouldFetch) {
            setIsLoading(false)
            setResolvedSrc(src)
            return () => controller.abort()
        }

        // Already resolved this session — use it without a request.
        const cached = getFreshCacheEntry(src)
        if (cached) {
            setResolvedSrc(cached.url)
            setIsLoading(false)
            return () => controller.abort()
        }

        // Viewport-gated: don't fetch until the consumer opts in (e.g. in view).
        if (!enabled) {
            setIsLoading(false)
            setResolvedSrc(fallbackSrc || '')
            return () => controller.abort()
        }

        setIsLoading(true)

        resolveProtectedSrc(src, controller.signal)
            .then(({ url }) => {
                setResolvedSrc(url)
                setIsLoading(false)
            })
            .catch((e) => {
                if (controller.signal.aborted) return
                setError(e)
                setResolvedSrc(fallbackSrc || '')
                setIsLoading(false)
            })

        // NOTE: object URLs live in the shared cache for the session, so we do
        // not revoke on unmount — other consumers may be using the same blob.
        return () => controller.abort()
    }, [src, shouldFetch, enabled, fallbackSrc])

    return { resolvedSrc, error, isProtected: shouldFetch, isLoading }
}
